import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import StorageOutlinedIcon from "@mui/icons-material/StorageOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import CheckIcon from "@mui/icons-material/Check";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import audioIcon from "../../../assets/images/chats/audioLogo.svg";
import imageFileIcon from "../../../assets/images/mediafiles/image.svg";
import pdfFileIcon from "../../../assets/images/mediafiles/pdf_icon.svg";
import mswordFileIcon from "../../../assets/images/mediafiles/mswordFile_logo.svg";
import excelFileIcon from "../../../assets/images/mediafiles/msExcel_.svg";
import powerpointFileIcon from "../../../assets/images/mediafiles/powerpointFile_logo.svg";
import { crossIcon, crossIconDiv, colorSchemes } from "../../../assets/commonFunction";
import { getFileIconUrl } from "../../../utils/findextension";
import { getCloudStorageProviderMeta } from "../../../utils/cloudStorageProviders";
import {
  getCloudStorageProviders,
  getCloudStorageAuthUrl,
  disconnectCloudStorage,
  listCloudStorageFiles,
  importCloudStorageFiles,
} from "../../../appStore/actions/cloudStorage/action";
import useAppSelectors from "../../../customHooks/useAppSelectors";

// Same-origin, opener-independent channel the OAuth popup broadcasts on when
// it finishes (see backend CloudStorageController.popupResultPage). Kept
// module-level so every open of the drawer reuses one channel instance.
const oauthChannel =
  typeof window !== "undefined" && "BroadcastChannel" in window
    ? new BroadcastChannel("arkchat-cloud-storage")
    : null;

const classes = {
  drawerBody: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    flexDirection: "column",
    height: "100%",
  },
  header: {
    backgroundColor: "#183243",
    color: "#0000",
    padding: "0 0.3rem",
    minHeight: "38px",
    flexShrink: 0,
  },
  iconDiv: crossIconDiv,
  iconn: { ...crossIcon, color: "#FFFFFF" },
};

const ATTACH_MODE = {
  LINK: "link",
  IMPORT: "import",
};

// Icon shown per nav-rail entry (view id -> icon), matching the semantics
// providers use for their own left sidebar. Falls back to a plain folder
// icon for any future view id a provider adds.
const VIEW_ICONS = {
  root: FolderOutlinedIcon,
  recent: AccessTimeOutlinedIcon,
  shared: PeopleAltOutlinedIcon,
  "shared-drives": StorageOutlinedIcon,
};

// Debounce for the live search-suggestions dropdown (below the search bar,
// similar to Drive's own search-as-you-type) — short enough to feel
// instant, long enough not to fire a request per keystroke.
const SUGGESTION_DEBOUNCE_MS = 300;
const SUGGESTION_LIMIT = 8;

// Checked before falling back to name-based sniffing (see fileTypeIcon
// below) — unlike regular chat attachments, Drive/OneDrive items always
// carry a real mimeType, and it's the only reliable signal for Google Docs/
// Sheets/Slides, whose names have no file extension at all ("Q3 Planning",
// not "Q3 Planning.gdoc").
const MIME_ICON_OVERRIDES = [
  { test: (m) => m.startsWith("image/"), icon: imageFileIcon },
  { test: (m) => m === "application/pdf", icon: pdfFileIcon },
  {
    test: (m) =>
      m === "application/vnd.google-apps.document" ||
      m.startsWith("application/msword") ||
      m.includes("wordprocessingml"),
    icon: mswordFileIcon,
  },
  {
    test: (m) =>
      m === "application/vnd.google-apps.spreadsheet" ||
      m.startsWith("application/vnd.ms-excel") ||
      m.includes("spreadsheetml"),
    icon: excelFileIcon,
  },
  {
    test: (m) =>
      m === "application/vnd.google-apps.presentation" ||
      m.startsWith("application/vnd.ms-powerpoint") ||
      m.includes("presentationml"),
    icon: powerpointFileIcon,
  },
];

// getFileIconUrl answers with the sentinel "audio" for audio extensions
const fileTypeIcon = (item) => {
  const mimeType = item.mimeType || "";
  const override = MIME_ICON_OVERRIDES.find(({ test }) => test(mimeType));
  if (override) return override.icon;
  const icon = getFileIconUrl(item.name);
  return icon === "audio" ? audioIcon : icon;
};

const formatFileSize = (size) => {
  if (!size) return null;
  return size > 1024 * 1024
    ? `${(size / (1024 * 1024)).toFixed(1)} MB`
    : `${Math.max(1, Math.round(size / 1024))} KB`;
};

// Generic cloud storage picker. Everything provider-specific lives behind the
// backend CloudStorageProvider implementations and the provider theme in
// utils/cloudStorageProviders.js — this drawer works for any provider id,
// but reads as that provider's own file browser: accent color, folder icon,
// grid layout, and a nav rail that switches between the provider's own
// collections (My Drive / Shared with me / Shared drives for Google,
// My files / Shared for OneDrive).
const CloudStorageDrawer = ({
  provider,
  handleCloseCloudDrawer,
  handleSendCloudAttachment,
  fileAttachment,
  attachment,
  onPreviewFile,
}) => {
  const providerMeta = getCloudStorageProviderMeta(provider);
  const { selectedGroup } = useAppSelectors();
  const views = providerMeta?.views?.length
    ? providerMeta.views
    : [{ id: "root", label: "Home" }];
  const accent = providerMeta?.accent || "#183243";
  const accentSoft = providerMeta?.accentSoft || "#EEF1F3";

  const [connectionState, setConnectionState] = useState("loading"); // loading | disconnected | connected
  const [accountEmail, setAccountEmail] = useState(null);
  const [activeView, setActiveView] = useState(views[0].id);
  const [files, setFiles] = useState([]);
  const [nextPageToken, setNextPageToken] = useState(null);
  // [{id: null, name, driveId: null}] — last entry is the open folder
  const [breadcrumbs, setBreadcrumbs] = useState([
    { id: null, name: views[0].label, driveId: null },
  ]);
  const [searchValue, setSearchValue] = useState("");
  const [activeSearch, setActiveSearch] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [attachMode, setAttachMode] = useState(ATTACH_MODE.LINK);
  const [isFetching, setIsFetching] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  // Thumbnail/iconLink URLs that failed to load — once marked, the tile
  // falls back to a bundled file-type icon instead of a broken image.
  const [brokenThumbnails, setBrokenThumbnails] = useState(() => new Set());
  const popupRef = useRef(null);
  const popupPollRef = useRef(null);
  const popupPollDeadlineRef = useRef(null);
  const searchInputRef = useRef(null);

  const usedSlots = fileAttachment?.length + attachment?.length;
  const totalSelected = usedSlots + selectedFiles.length;
  const isCheckBoxDisabled = totalSelected >= 5;

  const loadFiles = useCallback(
    async ({ folderId, driveId, search, view, pageToken, append = false } = {}) => {
      setIsFetching(true);
      setErrorMsg("");
      try {
        const result = await listCloudStorageFiles(provider, {
          folderId,
          driveId,
          search,
          view,
          pageToken,
        });
        setFiles((prev) =>
          append ? [...prev, ...(result?.files || [])] : result?.files || []
        );
        setNextPageToken(result?.nextPageToken || null);
        setConnectionState("connected");
      } catch (err) {
        if (err?.response?.status === 401) {
          setConnectionState("disconnected");
        } else {
          setErrorMsg("Could not load files. Please try again.");
        }
      } finally {
        setIsFetching(false);
      }
    },
    [provider]
  );

  // Re-checks connection status and, if connected, (re)loads the root of the
  // default view. Shared by the initial mount, the OAuth result channel, and
  // the popup-closed poll fallback below — all three roads lead here so the
  // drawer converges on the right state no matter which one fires first.
  const refreshConnectionStatus = useCallback(async () => {
    try {
      const providers = await getCloudStorageProviders();
      const status = providers?.find((item) => item.provider === provider);
      if (status?.connected) {
        setAccountEmail(status?.accountEmail || null);
        setErrorMsg("");
        await loadFiles({ view: views[0].id });
      } else {
        setConnectionState("disconnected");
      }
    } catch (err) {
      setConnectionState("disconnected");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider, loadFiles]);

  const stopPopupPoll = () => {
    if (popupPollRef.current) {
      clearInterval(popupPollRef.current);
      popupPollRef.current = null;
    }
    popupPollDeadlineRef.current = null;
  };

  // Single lightweight status check, shared by the poll interval below and
  // the focus/visibility fallback further down.
  const pollConnected = useCallback(async () => {
    try {
      const providers = await getCloudStorageProviders();
      const status = providers?.find((item) => item.provider === provider);
      return Boolean(status?.connected);
    } catch (err) {
      return false;
    }
  }, [provider]);

  // On open (or provider switch): check connection status once, then load
  // the default view's root folder.
  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      const defaultView = providerMeta?.views?.[0] || { id: "root", label: "Home" };
      setConnectionState("loading");
      setFiles([]);
      setSelectedFiles([]);
      setActiveView(defaultView.id);
      setBreadcrumbs([{ id: null, name: defaultView.label, driveId: null }]);
      setSearchValue("");
      setActiveSearch("");
      setAttachMode(ATTACH_MODE.LINK);
      setErrorMsg("");
      setBrokenThumbnails(new Set());
      try {
        const providers = await getCloudStorageProviders();
        if (cancelled) return;
        const status = providers?.find((item) => item.provider === provider);
        if (status?.connected) {
          setAccountEmail(status?.accountEmail || null);
          await loadFiles({ view: defaultView.id });
        } else {
          setConnectionState("disconnected");
        }
      } catch (err) {
        if (!cancelled) setConnectionState("disconnected");
      }
    };
    if (provider) init();
    return () => {
      cancelled = true;
      stopPopupPoll();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider, loadFiles]);

  // The OAuth popup reports back over BroadcastChannel (primary — works
  // regardless of window.opener) and postMessage (kept as a harmless extra).
  // See CloudStorageController.popupResultPage for why opener alone isn't
  // reliable: Google/Microsoft's own login pages sever it via COOP.
  useEffect(() => {
    const handleResult = (data) => {
      if (data?.source !== "arkchat-cloud-storage" || data?.provider !== provider) {
        return;
      }
      stopPopupPoll();
      if (data?.status === "connected") {
        setConnectionState("loading");
        refreshConnectionStatus();
      } else {
        setErrorMsg(data?.message || "Connection failed. Please try again.");
        setConnectionState("disconnected");
      }
    };

    const onWindowMessage = (event) => handleResult(event?.data);
    const onChannelMessage = (event) => handleResult(event?.data);

    window.addEventListener("message", onWindowMessage);
    oauthChannel?.addEventListener("message", onChannelMessage);
    return () => {
      window.removeEventListener("message", onWindowMessage);
      oauthChannel?.removeEventListener("message", onChannelMessage);
    };
  }, [provider, refreshConnectionStatus]);

  // Extra fallback on top of the channel/poll above: if the user manually
  // switches back to this tab while a popup connect attempt is outstanding
  // (popupPollRef set), check status immediately instead of waiting for the
  // next poll tick — covers the common case where they alt-tab back right
  // after the popup auto-closes.
  useEffect(() => {
    const handleFocusReturn = () => {
      if (!popupPollRef.current) return;
      pollConnected().then((connected) => {
        if (connected) {
          stopPopupPoll();
          refreshConnectionStatus();
        }
      });
    };
    window.addEventListener("focus", handleFocusReturn);
    document.addEventListener("visibilitychange", handleFocusReturn);
    return () => {
      window.removeEventListener("focus", handleFocusReturn);
      document.removeEventListener("visibilitychange", handleFocusReturn);
    };
  }, [pollConnected, refreshConnectionStatus]);

  // Live search suggestions (below the search bar, similar to Drive's own
  // search-as-you-type) — debounced so it doesn't fire a request per
  // keystroke, and only while the box is actually focused so it doesn't
  // re-fire from programmatic value changes (e.g. handleSearch resetting).
  useEffect(() => {
    if (!isSearchFocused || connectionState !== "connected") return undefined;
    const trimmed = searchValue.trim();
    if (!trimmed) {
      setSuggestions([]);
      return undefined;
    }
    const driveId = breadcrumbs[breadcrumbs.length - 1]?.driveId || undefined;
    const timer = setTimeout(async () => {
      try {
        const result = await listCloudStorageFiles(provider, {
          search: trimmed,
          driveId,
          view: activeView,
          pageSize: SUGGESTION_LIMIT,
        });
        setSuggestions(result?.files || []);
      } catch (err) {
        setSuggestions([]);
      }
    }, SUGGESTION_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchValue, isSearchFocused, connectionState, provider, activeView, breadcrumbs]);

  const handleConnect = async () => {
    setErrorMsg("");
    try {
      const { url } = await getCloudStorageAuthUrl(provider);
      const width = 520;
      const height = 640;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      const popup = window.open(
        url,
        `arkchat-${provider}-oauth`,
        `width=${width},height=${height},left=${left},top=${top}`
      );

      if (!popup) {
        setErrorMsg(
          "The connection popup was blocked. Please allow popups for this site and try again."
        );
        return;
      }

      popupRef.current = popup;

      // Last-resort fallback: if neither BroadcastChannel nor postMessage
      // gets through, this guarantees the drawer still refreshes without the
      // user having to close and reopen it. It polls connection status
      // directly rather than trusting a single `popup.closed` read — Google's
      // and Microsoft's own login pages set their own strict
      // Cross-Origin-Opener-Policy, and on some browsers that makes `.closed`
      // read true on the very first tick even though the user is still
      // mid-login (which used to make this give up before the flow had
      // actually finished). Requiring two consecutive closed reads filters
      // that out; the 2-minute cap is just a backstop against polling forever
      // if the user abandons the popup.
      stopPopupPoll();
      popupPollDeadlineRef.current = Date.now() + 120000;
      let consecutiveClosedReads = 0;
      popupPollRef.current = setInterval(async () => {
        const connected = await pollConnected();
        if (connected) {
          stopPopupPoll();
          refreshConnectionStatus();
          return;
        }
        consecutiveClosedReads = popup.closed ? consecutiveClosedReads + 1 : 0;
        if (consecutiveClosedReads >= 2 || Date.now() > popupPollDeadlineRef.current) {
          stopPopupPoll();
        }
      }, 900);
    } catch (err) {
      setErrorMsg(
        err?.response?.data?.message ||
        "Could not start the connection. Please try again."
      );
    }
  };

  const handleDisconnect = async () => {
    try {
      await disconnectCloudStorage(provider);
      setConnectionState("disconnected");
      setAccountEmail(null);
      setFiles([]);
      setSelectedFiles([]);
    } catch (err) {
      setErrorMsg("Could not disconnect. Please try again.");
    }
  };

  const currentFolder = breadcrumbs[breadcrumbs.length - 1];

  const handleViewChange = (viewId) => {
    if (viewId === activeView) return;
    const view = views.find((item) => item.id === viewId) || views[0];
    setActiveView(view.id);
    setBreadcrumbs([{ id: null, name: view.label, driveId: null }]);
    setSearchValue("");
    setActiveSearch("");
    // Clear the current list so the loader below shows instead of leaving
    // the previous section's files on screen until the new ones arrive.
    setFiles([]);
    setNextPageToken(null);
    loadFiles({ view: view.id });
  };

  const handleOpenFolder = (folder) => {
    // A shared-drive tile carries its own driveId; a normal folder inherits
    // whatever drive context (if any) its parent crumb already had.
    const driveId = folder.driveId || currentFolder?.driveId || null;
    setBreadcrumbs([...breadcrumbs, { id: folder.id, name: folder.name, driveId }]);
    setSearchValue("");
    setActiveSearch("");
    setFiles([]);
    setNextPageToken(null);
    loadFiles({ folderId: folder.id, driveId: driveId || undefined, view: activeView });
  };

  const handleBreadcrumbClick = (index) => {
    const target = breadcrumbs[index];
    setBreadcrumbs(breadcrumbs.slice(0, index + 1));
    setSearchValue("");
    setActiveSearch("");
    setFiles([]);
    setNextPageToken(null);
    loadFiles({
      folderId: target.id || undefined,
      driveId: target.driveId || undefined,
      view: activeView,
    });
  };

  const handleSearch = () => {
    setSuggestions([]);
    searchInputRef.current?.blur();
    if (!searchValue.trim()) {
      setActiveSearch("");
      loadFiles({
        folderId: currentFolder?.id || undefined,
        driveId: currentFolder?.driveId || undefined,
        view: activeView,
      });
      return;
    }
    setActiveSearch(searchValue.trim());
    loadFiles({
      search: searchValue.trim(),
      driveId: currentFolder?.driveId || undefined,
      view: activeView,
    });
  };

  const handleClearSearch = () => {
    setSearchValue("");
    setActiveSearch("");
    setSuggestions([]);
    loadFiles({
      folderId: currentFolder?.id || undefined,
      driveId: currentFolder?.driveId || undefined,
      view: activeView,
    });
  };

  const handleShowMore = () => {
    loadFiles({
      folderId: activeSearch ? undefined : currentFolder?.id || undefined,
      driveId: currentFolder?.driveId || undefined,
      search: activeSearch || undefined,
      view: activeView,
      pageToken: nextPageToken,
      append: true,
    });
  };

  const handleToggleFile = (file) => {
    const isSelected = selectedFiles.some((item) => item.id === file.id);
    if (isSelected) {
      setSelectedFiles(selectedFiles.filter((item) => item.id !== file.id));
    } else {
      if (isCheckBoxDisabled) return;
      setSelectedFiles([...selectedFiles, file]);
    }
  };

  const handleTileClick = (item) => {
    if (item.isFolder) {
      handleOpenFolder(item);
    } else {
      handleToggleFile(item);
    }
  };

  // Opens the file in the same preview panel used for regular chat
  // attachments (FilePreview, mounted in Tabs/index.jsx). The file isn't in
  // Arkchat's own storage yet, so it's handed over via `cloudPreview`
  // instead of `location`/`mimeType` — FilePreview embeds the provider's own
  // preview viewer for it (see getCloudStoragePreviewUrl) rather than
  // fetching it through the regular attachment-download path.
  const handlePreviewFile = (event, item) => {
    event.stopPropagation();
    onPreviewFile?.({
      name: item.name,
      cloudPreview: {
        provider,
        fileId: item.id,
        driveId: item.driveId || undefined,
      },
    });
  };

  // Clicking a suggestion: a folder navigates into it (closing the
  // dropdown), a file toggles its selection and leaves the dropdown open so
  // several matches can be picked in a row without retyping the search.
  const handleSuggestionClick = (item) => {
    if (item.isFolder) {
      setSuggestions([]);
      searchInputRef.current?.blur();
      handleOpenFolder(item);
    } else {
      handleToggleFile(item);
    }
  };

  const handleThumbnailError = (fileId) => {
    setBrokenThumbnails((prev) => {
      if (prev.has(fileId)) return prev;
      const next = new Set(prev);
      next.add(fileId);
      return next;
    });
  };

  const handleClose = () => {
    setSelectedFiles([]);
    handleCloseCloudDrawer();
  };

  const handleClickDone = async () => {
    if (selectedFiles.length === 0 || isImporting) return;
    setErrorMsg("");

    if (attachMode === ATTACH_MODE.LINK) {
      const attachedLinks = selectedFiles.map((file) => ({
        thumbnail: file.thumbnail || "",
        name: file.name,
        location: file.webViewLink,
        id: file.id,
      }));
      handleSendCloudAttachment(attachedLinks);
      return;
    }

    // Import mode: the backend copies the files into Arkchat storage and
    // answers with the regular attachment shape ({location, name, mimeType}).
    setIsImporting(true);
    try {
      const imported = await importCloudStorageFiles(
        provider,
        selectedGroup?.pnId,
        selectedFiles
      );
      const attachedFiles = (imported || []).map((item) => ({
        ...item,
        thumbnail: "",
        id: item.location,
      }));
      handleSendCloudAttachment(attachedFiles);
    } catch (err) {
      setErrorMsg(
        err?.response?.data?.message ||
        "Could not import the selected files. Please try again."
      );
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <React.Fragment>
      <Box sx={classes.drawerBody}>
        <Stack flex={1} minHeight={0} overflow={"hidden"}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={classes.header}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography sx={classes.iconDiv}>
                <CloseIcon sx={classes.iconn} onClick={handleClose} />
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1}>
                {providerMeta?.icon && (
                  <img
                    src={providerMeta.icon}
                    alt={providerMeta?.label}
                    width={18}
                    height={18}
                  />
                )}
                <Typography color={"#FFFFFF"} fontWeight={400} fontSize={"14px"}>
                  {providerMeta?.label || "Cloud storage"}
                </Typography>
              </Stack>
            </Stack>
            {connectionState === "connected" && (
              <Stack direction="row" alignItems="center" spacing={1}>
                {accountEmail && (
                  <Typography
                    fontSize={"0.8rem"}
                    color={"#FFFFFF"}
                    sx={{
                      maxWidth: "9rem",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={accountEmail}
                  >
                    {accountEmail}
                  </Typography>
                )}
                <Typography
                  onClick={handleDisconnect}
                  fontSize={"0.75rem"}
                  color={"rgba(255, 255, 255, 0.65)"}
                  sx={{
                    cursor: "pointer",
                    flexShrink: 0,
                    "&:hover": { color: "#FFFFFF", textDecoration: "underline" },
                  }}
                >
                  Disconnect
                </Typography>
              </Stack>
            )}
          </Stack>

          {connectionState === "loading" && (
            <Stack alignItems="center" mt={"4rem"}>
              <CircularProgress size={28} sx={{ color: "#5B5B5B" }} />
            </Stack>
          )}

          {connectionState === "disconnected" && (
            <Stack
              alignItems="center"
              height={"100%"}
              px={"2rem"}
              pb={"2.5rem"}
            >
              <Stack
                alignItems="center"
                justifyContent="center"
                spacing={2}
                flex={1}
                minHeight={0}
              >
                {providerMeta?.icon && (
                  <img
                    src={providerMeta.icon}
                    alt={providerMeta?.label}
                    width={48}
                    height={48}
                  />
                )}
                <Typography textAlign="center" fontSize={"0.87rem"}>
                  Connect your {providerMeta?.label} account<br />
                  to browse and share your files in Arkchat.<br />
                  You only need to do this once.
                </Typography>
                {errorMsg && (
                  <Typography fontSize={"0.75rem"} color={colorSchemes.themeRedColor}>
                    {errorMsg}
                  </Typography>
                )}
              </Stack>
              <Stack alignItems="center" spacing={1} width={"100%"}>
                <Button
                  onClick={handleConnect}
                  style={{
                    background: colorSchemes.themeRedColor,
                    color: "white",
                    borderRadius: "2px",
                    textTransform: "none",
                    width: "40%",
                    padding: "0.6rem",
                  }}
                  sx={{ "&:hover": { background: "#B5000E" } }}
                  variant="contained"
                >
                  Connect
                </Button>
                <Typography fontSize={"0.75rem"} color={"#5B5B5B"}>
                  You can disconnect any time
                </Typography>
              </Stack>
            </Stack>
          )}

          {connectionState === "connected" && (
            <>
              <Stack
                width={"92%"}
                margin={"auto"}
                mt={"0.6rem"}
                flexShrink={0}
                position={"relative"}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    background: "#F1F3F4",
                    borderRadius: "999px",
                    padding: "0.4rem 0.9rem",
                    border: "1px solid transparent",
                    "&:focus-within": {
                      background: "#fff",
                      border: `1px solid ${accent}`,
                      boxShadow: `0 1px 4px ${accent}33`,
                    },
                  }}
                >
                  <SearchIcon sx={{ fontSize: 18, color: "#5B5B5B" }} />
                  <input
                    autoFocus
                    ref={searchInputRef}
                    value={searchValue}
                    onChange={(event) => setSearchValue(event.target.value)}
                    onFocus={() => setIsSearchFocused(true)}
                    onBlur={() => setIsSearchFocused(false)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") handleSearch();
                      if (event.key === "Escape") {
                        setSuggestions([]);
                        searchInputRef.current?.blur();
                      }
                    }}
                    placeholder={providerMeta?.searchPlaceholder || "Search"}
                    style={{
                      border: "none",
                      outline: "none",
                      background: "transparent",
                      flex: 1,
                      fontSize: "0.85rem",
                      fontFamily: "inherit",
                    }}
                  />
                  {searchValue && (
                    <CloseIcon
                      onClick={handleClearSearch}
                      sx={{
                        fontSize: 16,
                        cursor: "pointer",
                        color: "#5B5B5B",
                        "&:hover": { color: "#000" },
                      }}
                    />
                  )}
                </Box>

                {/* Live suggestions, similar to Drive's search-as-you-type.
                    onMouseDown (not onClick) + preventDefault stops the input
                    from blurring before the click registers, so the dropdown
                    doesn't vanish out from under the cursor. */}
                {isSearchFocused && suggestions.length > 0 && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      right: 0,
                      marginTop: "0.35rem",
                      background: "#fff",
                      borderRadius: "8px",
                      boxShadow: "0 2px 12px rgba(0,0,0,0.18)",
                      zIndex: 20,
                      overflow: "hidden",
                      maxHeight: "16rem",
                      overflowY: "auto",
                    }}
                  >
                    {suggestions.map((item) => {
                      const isSelected = selectedFiles.some(
                        (selected) => selected.id === item.id
                      );
                      return (
                        <Stack
                          key={item.id}
                          direction="row"
                          alignItems="center"
                          spacing={1}
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => handleSuggestionClick(item)}
                          sx={{
                            padding: "0.5rem 0.8rem",
                            cursor: "pointer",
                            background: isSelected ? accentSoft : "transparent",
                            "&:hover": {
                              background: isSelected ? accentSoft : "#F5F5F5",
                            },
                          }}
                        >
                          <img
                            src={
                              item.isFolder
                                ? providerMeta?.folderIcon
                                : item.thumbnail || item.iconLink || fileTypeIcon(item)
                            }
                            alt=""
                            width={18}
                            height={18}
                            style={{ objectFit: "contain", flexShrink: 0 }}
                          />
                          <Typography
                            noWrap
                            fontSize={"0.8rem"}
                            flex={1}
                            title={item.name}
                          >
                            {item.name}
                          </Typography>
                          {isSelected && (
                            <CheckIcon sx={{ fontSize: 16, color: accent, flexShrink: 0 }} />
                          )}
                        </Stack>
                      );
                    })}
                  </Box>
                )}
              </Stack>

              <Stack flex={1} minHeight={0} mt={"0.5rem"}>
                {/* View tabs: switch between the provider's own collections
                    (My Drive / Recent / Shared with me / Shared drives, etc.)
                    Horizontal + scrollable so it degrades gracefully if a
                    provider ever adds more views than fit the drawer width. */}
                <Stack
                  direction="row"
                  alignItems="center"
                  flexShrink={0}
                  px={"0.9rem"}
                  sx={{
                    borderBottom: "1px solid #EEEEEE",
                    overflowX: "auto",
                    scrollbarWidth: "none",
                    "&::-webkit-scrollbar": { display: "none" },
                  }}
                >
                  {views.map((view) => {
                    const ViewIcon = VIEW_ICONS[view.id] || FolderOutlinedIcon;
                    const isActive = view.id === activeView;
                    return (
                      <Stack
                        key={view.id}
                        direction="row"
                        alignItems="center"
                        spacing={0.6}
                        onClick={() => handleViewChange(view.id)}
                        sx={{
                          cursor: "pointer",
                          flexShrink: 0,
                          padding: "0.55rem 0.7rem",
                          marginBottom: "-1px",
                          borderBottom: `2px solid ${
                            isActive ? accent : "transparent"
                          }`,
                          "&:hover": {
                            background: isActive ? "transparent" : "#F5F5F5",
                          },
                        }}
                      >
                        <ViewIcon
                          sx={{
                            fontSize: 16,
                            color: isActive ? accent : "#5B5B5B",
                          }}
                        />
                        <Typography
                          fontSize={"0.78rem"}
                          fontWeight={isActive ? 600 : 400}
                          color={isActive ? accent : "#3C4043"}
                          noWrap
                        >
                          {view.label}
                        </Typography>
                      </Stack>
                    );
                  })}
                </Stack>

                <Stack flex={1} minHeight={0} overflow={"hidden"}>
                  {/* Breadcrumb navigation (hidden while a search is active) */}
                  {!activeSearch && (
                    <Stack
                      direction="row"
                      flexWrap="wrap"
                      alignItems="center"
                      px={"0.9rem"}
                      mt={"0.5rem"}
                      flexShrink={0}
                    >
                      {breadcrumbs.map((crumb, index) => (
                        <Stack
                          direction="row"
                          alignItems="center"
                          key={`${crumb.id || "root"}-${index}`}
                        >
                          <Typography
                            fontSize={"0.8rem"}
                            color={
                              index === breadcrumbs.length - 1 ? "#202124" : accent
                            }
                            fontWeight={index === breadcrumbs.length - 1 ? 600 : 500}
                            sx={{
                              cursor:
                                index === breadcrumbs.length - 1
                                  ? "default"
                                  : "pointer",
                              maxWidth: "8rem",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              "&:hover":
                                index === breadcrumbs.length - 1
                                  ? {}
                                  : { textDecoration: "underline" },
                            }}
                            onClick={() =>
                              index !== breadcrumbs.length - 1 &&
                              handleBreadcrumbClick(index)
                            }
                          >
                            {crumb.name}
                          </Typography>
                          {index < breadcrumbs.length - 1 && (
                            <Typography
                              fontSize={"0.8rem"}
                              color={"#9AA0A6"}
                              px={"0.35rem"}
                            >
                              /
                            </Typography>
                          )}
                        </Stack>
                      ))}
                    </Stack>
                  )}

                  {activeSearch && (
                    <Stack px={"0.9rem"} mt={"0.5rem"} flexShrink={0}>
                      <Typography fontSize={"0.75rem"} color={"#5B5B5B"}>
                        Results for "{activeSearch}"
                      </Typography>
                    </Stack>
                  )}

                  {errorMsg && (
                    <Stack px={"0.9rem"} mt={"0.3rem"} flexShrink={0}>
                      <Typography fontSize={"0.75rem"} color={"#DB0011"}>
                        {errorMsg}
                      </Typography>
                    </Stack>
                  )}

                  <Box
                    mt={"0.4rem"}
                    flex={1}
                    minHeight={0}
                    overflow={"auto"}
                    sx={{
                      "&::-webkit-scrollbar": {
                        width: "6px",
                      },
                      "&::-webkit-scrollbar-track": {
                        background: "#f1f1f1",
                      },
                      "&::-webkit-scrollbar-thumb": {
                        background: "#888",
                        borderRadius: "3px",
                      },
                    }}
                  >
                    {isFetching && files?.length === 0 ? (
                      <Stack alignItems="center" mt={"2rem"}>
                        <CircularProgress size={24} sx={{ color: accent }} />
                      </Stack>
                    ) : files?.length === 0 ? (
                      <Typography
                        textAlign="center"
                        mt={"2rem"}
                        fontSize={"0.85rem"}
                        color={"#5B5B5B"}
                      >
                        {activeSearch
                          ? "No files found."
                          : "Nothing here."}
                      </Typography>
                    ) : (
                      <Box
                        sx={{
                          display: "grid",
                          // Fixed-width tracks (not `1fr`) so a handful of
                          // files stay compact and left-aligned instead of
                          // stretching to fill the row with empty-looking
                          // whitespace.
                          gridTemplateColumns:
                            "repeat(auto-fill, minmax(5rem, 5rem))",
                          gap: "0.3rem",
                          padding: "0.35rem 0.7rem",
                        }}
                      >
                        {files?.map((item) => {
                          const isSelected = selectedFiles.some(
                            (selected) => selected.id === item.id
                          );
                          const sizeLabel = !item.isFolder
                            ? formatFileSize(item.size)
                            : null;
                          const remotePreview =
                            !item.isFolder && !brokenThumbnails.has(item.id)
                              ? item.thumbnail || item.iconLink
                              : null;
                          const tileIcon = item.isFolder
                            ? providerMeta?.folderIcon
                            : fileTypeIcon(item);

                          return (
                            <Box
                              key={item.id}
                              onClick={() => handleTileClick(item)}
                              sx={{
                                position: "relative",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: "0.25rem",
                                padding: "0.4rem 0.3rem 0.35rem 0.3rem",
                                borderRadius: "8px",
                                cursor: "pointer",
                                border: `1px solid ${isSelected ? accent : "transparent"}`,
                                background: isSelected ? accentSoft : "transparent",
                                "&:hover": {
                                  background: isSelected ? accentSoft : "#F5F5F5",
                                  "& .cloud-tile-checkbox": { opacity: 1 },
                                  "& .cloud-tile-preview": { opacity: 1 },
                                },
                              }}
                            >
                              {!item.isFolder && (
                                <Checkbox
                                  className="cloud-tile-checkbox"
                                  size="small"
                                  checked={isSelected}
                                  disabled={isCheckBoxDisabled && !isSelected}
                                  onClick={(event) => event.stopPropagation()}
                                  onChange={() => handleToggleFile(item)}
                                  sx={{
                                    position: "absolute",
                                    top: 1,
                                    left: 1,
                                    padding: "2px",
                                    opacity: isSelected ? 1 : 0,
                                    transition: "opacity 0.1s",
                                    "& .MuiSvgIcon-root": { fontSize: 17 },
                                    "&.Mui-checked": { color: accent },
                                  }}
                                />
                              )}
                              {!item.isFolder && onPreviewFile && (
                                <Box
                                  className="cloud-tile-preview"
                                  onClick={(event) => handlePreviewFile(event, item)}
                                  title="Preview"
                                  sx={{
                                    position: "absolute",
                                    top: 1,
                                    right: 1,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: 21,
                                    height: 21,
                                    borderRadius: "50%",
                                    opacity: 0,
                                    transition: "opacity 0.1s",
                                    background: "#FFFFFF",
                                    boxShadow: "0 1px 3px rgba(0,0,0,0.25)",
                                    "&:hover": { background: accentSoft },
                                  }}
                                >
                                  <VisibilityOutlinedIcon
                                    sx={{ fontSize: 13, color: "#3C4043" }}
                                  />
                                </Box>
                              )}
                              <Box
                                sx={{
                                  width: 34,
                                  height: 34,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <img
                                  style={{
                                    maxWidth: "100%",
                                    maxHeight: "100%",
                                    objectFit: "contain",
                                  }}
                                  src={remotePreview || tileIcon}
                                  onError={
                                    remotePreview
                                      ? () => handleThumbnailError(item.id)
                                      : undefined
                                  }
                                  alt=""
                                />
                              </Box>
                              <Typography
                                fontSize={"0.66rem"}
                                textAlign="center"
                                title={item.name}
                                sx={{
                                  width: "100%",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {item.name}
                              </Typography>
                              {sizeLabel && (
                                <Typography fontSize={"0.56rem"} color={"#5B5B5B"}>
                                  {sizeLabel}
                                </Typography>
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    )}

                    {nextPageToken && !isFetching && files?.length > 0 && (
                      <Stack alignItems="center" my={"0.5rem"}>
                        <Typography
                          onClick={handleShowMore}
                          sx={{ cursor: "pointer" }}
                          variant="caption"
                          fontSize="0.75rem"
                          color={accent}
                        >
                          Show More
                        </Typography>
                      </Stack>
                    )}
                  </Box>
                </Stack>
              </Stack>
            </>
          )}
        </Stack>

        {connectionState === "connected" && (
          <Stack
            alignItems="center"
            spacing={1}
            padding="0.4rem"
            margin={"0 auto"}
            width={"100%"}
            position={"relative"}
            mb={"0.4rem"}
            flexShrink={0}
            borderTop={"1px solid #E3E1E1"}
          >
            <RadioGroup
              row
              value={attachMode}
              onChange={(event) => setAttachMode(event.target.value)}
            >
              <FormControlLabel
                value={ATTACH_MODE.LINK}
                control={
                  <Radio
                    size="small"
                    sx={{ "&.Mui-checked": { color: accent } }}
                  />
                }
                label={
                  <Typography fontSize={"0.75rem"}>Share as link</Typography>
                }
              />
              <FormControlLabel
                value={ATTACH_MODE.IMPORT}
                control={
                  <Radio
                    size="small"
                    sx={{ "&.Mui-checked": { color: accent } }}
                  />
                }
                label={
                  <Typography fontSize={"0.75rem"}>
                    Upload copy
                  </Typography>
                }
              />
            </RadioGroup>

            <Button
              onClick={handleClickDone}
              disabled={selectedFiles.length === 0 || isImporting}
              style={{
                background:
                  selectedFiles.length === 0 || isImporting ? "#8C8C8C" : accent,
                color: "white",
                borderRadius: "4px",
                minWidth: "6rem",
                textTransform: "none",
              }}
              variant="contained"
            >
              {isImporting ? (
                <CircularProgress size={18} sx={{ color: "white" }} />
              ) : (
                "Done"
              )}
            </Button>
            <Box position={"absolute"} right={"1.5rem"} bottom={"0.9rem"}>
              <Typography fontSize={"0.85rem"}>{totalSelected}/5</Typography>
            </Box>
          </Stack>
        )}
      </Box>
    </React.Fragment>
  );
};

export default CloudStorageDrawer;
